import { useContext, useState } from 'react';
import { Body1Strong } from '@fluentui/react-components';
import { HistoryRegular, PeopleRegular } from '@fluentui/react-icons';
import { ChangeNoteRow, NavOverviewLayout, VersionLinkRow } from '@shared/components';
import { DateLabel } from '@shared/components/dates/DateLabel';
import type { TOverflowButtonParam } from '@shared/components/Overflow';
import { DRAFT_VERSION_VALUE } from '@shared/components/VersionDropdown';
import { useSearchParams } from '@shared/hooks/useSearchParams.hook';
import { GENERIC_ERROR_MESSAGE } from '@shared/i18n/messages';
import { TERMINOLOGY } from '@shared/i18n/terminology';
import { CHANGE_TEMPLATE_VERSION_PARAM } from '../constants';
import UserLabel from '@modules/users/components/UserLabel';
import { AccessDialog, type TAccessDialogState, AccessType } from '@modules/access';
import { AUTO_EDIT_QUERY_PARAM, AUTO_EDIT_QUERY_VALUE } from '@modules/items/constants';
import { DocumentContext, useEditNavActions } from '@modules/documents';
import { useCreateChangeTemplateDraft } from '../api';
import {
  isTChangeTemplateVersion,
  type TChangeTemplateDetails,
  type TChangeTemplateDraft,
  type TChangeTemplateVersion,
} from '../types';

type TProps = {
  details: TChangeTemplateDetails;
  selected: TChangeTemplateDraft | TChangeTemplateVersion;
  canEdit: boolean;
  // Why a user who cannot edit sees a locked edit button, empty when they can edit.
  editLockedReason: string;
  isEditorPending: boolean;
  onComplete: () => void;
  templateVersion?: string;
};

const ChangeTemplateNavOverview = ({
  details: { changeTemplate, permissions },
  selected,
  canEdit,
  editLockedReason,
  isEditorPending,
  onComplete,
  templateVersion,
}: TProps) => {
  const { versions } = changeTemplate;
  const hasDraft = !!changeTemplate.draft;
  const { changeMany } = useSearchParams();
  // Missing while no document is shown.
  const documentContext = useContext(DocumentContext);
  const createDraft = useCreateChangeTemplateDraft(changeTemplate._id);
  const [accessDialogState, setAccessDialogState] = useState<TAccessDialogState>();
  const currentVersion = hasDraft ? DRAFT_VERSION_VALUE : versions[0]?.id;

  const getVersionParams = (versionId: string | undefined) => ({
    // The current draft or version is shown without a version in the URL.
    [CHANGE_TEMPLATE_VERSION_PARAM]: versionId === currentVersion ? undefined : versionId,
    view: undefined,
    [AUTO_EDIT_QUERY_PARAM]: undefined,
  });

  const showVersion = async (versionId: string | undefined) => {
    const params = getVersionParams(versionId);
    if (params[CHANGE_TEMPLATE_VERSION_PARAM] === templateVersion) return;
    // The leave guard sees only path changes, so save before changing the version.
    if (documentContext?.isEdit && !(await documentContext.save())) return;
    changeMany(params);
  };

  const { editAction, completeAction } = useEditNavActions({
    hasDraft,
    disabledReason: editLockedReason || (documentContext?.hasUnsupportedContent ? GENERIC_ERROR_MESSAGE : ''),
    viewLatest: templateVersion ? () => showVersion(currentVersion) : undefined,
    // Like a Document's Create draft, it starts editing the new draft.
    createDraft: {
      onClick: () =>
        createDraft.mutate(undefined, {
          // Once the draft exists, a version in the URL is no longer the current one.
          onSuccess: () =>
            changeMany({ [CHANGE_TEMPLATE_VERSION_PARAM]: undefined, [AUTO_EDIT_QUERY_PARAM]: AUTO_EDIT_QUERY_VALUE }),
        }),
      isPending: createDraft.isPending,
    },
    keepsEditIntent: true,
    isEditorPending,
    onComplete: canEdit && hasDraft ? onComplete : undefined,
  });

  const actions: TOverflowButtonParam[] = [editAction];
  if (completeAction) actions.push(completeAction);
  // Placed like a Document's Changes button.
  actions.push({
    children: 'Changes',
    icon: <HistoryRegular />,
    disabled: true,
    disabledMessage: `Comparing ${TERMINOLOGY.changeTemplate.lower} versions is not available yet.`,
  });
  if (permissions.accessRight >= AccessType.MANAGE)
    actions.push({
      children: 'Manage access',
      icon: <PeopleRegular />,
      onClick: () =>
        setAccessDialogState({
          isOpen: true,
          id: changeTemplate._id,
          nodeType: 'CHANGE_TEMPLATE',
          name: changeTemplate.title,
        }),
    });

  return (
    <>
      <NavOverviewLayout
        actions={actions}
        versionDropdownProps={{
          versions,
          hasDraft,
          selectedVersion: templateVersion ?? currentVersion,
          onOptionSelect: (_, { optionValue }) => showVersion(optionValue),
        }}
        itemRows={
          <VersionLinkRow
            label="Latest version"
            versionId={versions[0]?.id}
            isCurrent={selected === versions[0]}
            getParams={getVersionParams}
            onSelect={showVersion}
          />
        }
      >
        {isTChangeTemplateVersion(selected) ? (
          <p className="flex">
            <span className="block w-32 flex-shrink-0">Completed:</span>
            <Body1Strong>
              <DateLabel>{selected.completedDate}</DateLabel>
            </Body1Strong>
          </p>
        ) : (
          <p className="flex">
            <span className="block w-32 flex-shrink-0">Modified:</span>
            <Body1Strong>
              <DateLabel>{changeTemplate.stats.modifiedDate}</DateLabel>
            </Body1Strong>
          </p>
        )}
        {selected.authors.length > 0 && (
          <p className="flex">
            <span className="block w-32 flex-shrink-0">Authors:</span>
            <Body1Strong>
              <span className="flex flex-col space-y-3 force-break-word">
                {selected.authors.map((userId) => (
                  <UserLabel id={userId} key={userId} />
                ))}
              </span>
            </Body1Strong>
          </p>
        )}
        {isTChangeTemplateVersion(selected) && <ChangeNoteRow note={selected.note} />}
      </NavOverviewLayout>
      <AccessDialog state={accessDialogState} setState={setAccessDialogState} />
    </>
  );
};

export default ChangeTemplateNavOverview;
